import {
	type CaptureFileJSONObject,
	type CollectionJSONObject,
	type CollectionSummaryJSONObject,
	type GeoPointJSON,
	IkeToken,
	ParseJSON,
	TimingEventAction,
	TimingEventType,
} from '@ikegps/core-api';
import {
	CaptureProviderHelpers,
	CollectionModel,
	CollectionModelBase,
	CollectionModelField,
	CollectionModelFieldAnchor,
	CollectionModelFieldFileTool,
	CollectionModelFieldImage,
	CollectionModelFieldText,
	IkeDrawState,
	IkeId,
} from '@ikegps/core-shared';
import { action, computed, observable, ObservableMap, reaction, runInAction, makeObservable } from 'mobx';
import { ModalService } from '../../components/modal/modal.service';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n/index';
import { Services } from '../../services';
import { type FieldJoin, FieldJoinMatch } from '../../services/field.join/field.join';
import { MobxState } from '../../state/mobx';
import { CurrentState } from '../../state/page.state';
import { AppStore } from '../../store';
import {
	FileUploadReader,
	ImageUploadReader,
	MAX_UPLOAD_FILE_SIZE_BYTE,
	MAX_UPLOAD_FILE_SIZE_MB,
	validateImageFile,
} from '../../util/upload.reader';
import { CSS as MultiListChooserCSS } from '../multi.list.chooser//multi.list.chooser.style';
import { MultiListChooserContent, MultiListChooserTitle } from '../multi.list.chooser/multi.list.chooser';
import { SpanLinkChooserState } from '../multi.list.chooser/span.link.chooser.state';
import { TagMapState } from '../tag.map/tag.map.state';
import { savePendingSpanTargets } from '../../services/subform.link/save.pending.span.targets';
import { CollectionChangeState } from './collection.change.state';
import type { ICollectionViewController } from './collection.view.ctrl.interface';

export class CollectionEditCtrl extends MobxState implements ICollectionViewController {
	@observable.ref changeState: CollectionChangeState | null = null;
	isFormOpen: ObservableMap<string, boolean> = observable.map<string, boolean>({}, { deep: false });
	isFieldDetailShown: ObservableMap<string, boolean> = observable.map<string, boolean>({}, { deep: false });

	lastCollectionId: string | null = null;

	@observable isAllExpanded: boolean = true;
	@observable isInEditMode: boolean = false;
	@observable isSaving: boolean = false;

	@observable isMetadataShown: boolean = false;
	@observable isZoomShown: boolean = true;
	@observable isPhotomeasure: boolean = false;
	allowedTypes: string[] = [];
	@observable isInUploadMode = false;
	@observable fieldsInLoadingState: string[] = [];

	@observable.shallow file: FileUploadReader | null = null;
	@observable.shallow image: ImageUploadReader | null = null;

	readonly kind = 'CollectionEditCtrl';

	static is(ctrl: ICollectionViewController): ctrl is CollectionEditCtrl {
		return ctrl.kind === 'CollectionEditCtrl';
	}

	mapState: TagMapState;

	constructor(state: TagMapState) {
		super();

		makeObservable(this);

		this.mapState = state;
		this.changeState = new CollectionChangeState();

		this.reaction((_) => this.collection, this.updateEditedCollection);
		this.reaction((_) => this.file && this.file.buffer, this.processFile);
		this.reaction((_) => this.image && this.image.buffer, this.processImage);

		Services.Window.bindNavigateAway(T.DISCARD_CHANGES_CONFIRM, this.stopNavigation);
	}

	override destroy() {
		super.destroy();
		if (this.changeState) {
			this.changeState.destroy();
		}
		Services.Window.unbindNavigateAway(this.stopNavigation);
		if (CurrentState.CollectionEdit.collectionRevision) {
			CurrentState.CollectionEdit.reset();
		}
		if (CurrentState.CollectionLink.hasModifiedTargets) {
			CurrentState.CollectionLink.discardAllModifiedTargets();
		}
	}

	get collection(): CollectionModel | null {
		return CurrentState.CollectionEdit.collection;
	}

	get isCollectionLoading() {
		if (!CurrentState.inspectedCollectionRevision) {
			return false;
		}
		return !AppStore.Collection.has(CurrentState.inspectedCollectionRevision);
	}

	get isCollectionSelected() {
		return CurrentState.inspectedCollectionRevision != null;
	}

	get isEditing() {
		return this.changeState != null && (this.isInEditMode || this.isPhotomeasure);
	}

	get isValid() {
		return this.changeState != null && this.changeState.isValid && !this.isInUploadMode;
	}

	get isEditable() {
		return Services.Token.has(IkeToken.FEATURE_MAP_EDIT);
	}

	get useColorCoding() {
		return Services.Token.has(IkeToken.PHOTOMEASURE_COLOR_CODING);
	}

	get isUploading() {
		return this.changeState != null && this.isInUploadMode;
	}

	@action clearOpenState() {
		this.isFormOpen.clear();
		this.isFieldDetailShown.clear();
	}

	@action.bound updateEditedCollection() {
		const currentCollection = this.collection;
		if (currentCollection == null) {
			this.lastCollectionId = null;
			if (this.changeState != null) {
				this.changeState.destroy();
			}
			this.changeState = null;
			this.isInEditMode = false;
		} else if (this.lastCollectionId !== currentCollection.revision) {
			this.clearOpenState();
			this.lastCollectionId = currentCollection.revision;
			if (this.changeState != null) {
				this.changeState.destroy();
			}
			this.changeState = new CollectionChangeState();
			this.isInEditMode = false;
		}
	}

	@action.bound abortChanges(e: Event) {
		e.preventDefault();
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.mapState.checkAndDiscardChanges().then((shouldDiscard) => {
			if (shouldDiscard && this.changeState != null) {
				this.discardAndDestroyEdit();
			}
		});
		return false;
	}

	@action private discardAndDestroyEdit() {
		if (this.changeState != null) {
			const draw = CurrentState.CollectionEdit.currentDraw;
			if (draw != null) {
				// Reset brightness state
				draw.state.setBrightness(draw.state.defaultBrightness);
				// Reset the image visibility state
				draw.state.setImageVisibilityHidden(draw.state.defaultImageVisibilityHidden);
				// Reset the crop state
				const crop = draw.state.defaultCrop;
				draw.state.setCrop(crop.x, crop.y, crop.width, crop.height);
			}
			//discard everything
			while (CurrentState.CollectionEdit.isDirty) {
				CurrentState.CollectionEdit.reset();
			}

			// Revert modified target collections
			if (CurrentState.CollectionLink.hasModifiedTargets) {
				CurrentState.CollectionLink.discardAllModifiedTargets();
			}

			// Switch displayed image to own captures if current image is discarded
			if (this.isPhotomeasure && this.collection != null && CurrentState.inspectedCaptureId != null) {
				const captureIds = Services.Collection.getCaptureIds(this.collection);
				if (captureIds.indexOf(CurrentState.inspectedCaptureId) === -1) {
					CurrentState.inspectedCaptureId = Services.Collection.getFirstCaptureId(this.collection);
				}
			}

			// After everything has been reset, display the crosshair depending if there are any measurements
			if (draw != null) {
				draw.state.setCrosshair(!draw.drawState.hasUserMeasurements());
			}
		}
		this.changeState = new CollectionChangeState();
		this.updateEditedCollection();
		this.isInEditMode = false;
	}

	@action.bound async saveChanges(e: Event) {
		e.preventDefault();

		if (!this.changeState?.isValid) {
			return;
		}

		// Snapshot modified target collections before saving
		const hasModifiedTargets = CurrentState.CollectionLink.hasModifiedTargets;
		const dirtyTargets = hasModifiedTargets ? CurrentState.CollectionLink.getModifiedTargetCollections() : [];

		if (!CurrentState.CollectionEdit.isDirty && dirtyTargets.length === 0) {
			return;
		}

		if (this.isSaving) {
			return;
		}

		const originalCollection = this.collection;
		const drawState = CurrentState.CollectionEdit.currentDraw?.state;

		if (!originalCollection) {
			return;
		}

		this.setSaving(true);

		try {
			Services.Analytics.trackTiming(TimingEventType.HOA, TimingEventAction.EXIT, originalCollection.id, new Date());
			const result = await Services.Collection.save(originalCollection);

			// Save modified target collections
			if (dirtyTargets.length > 0) {
				const targetResult = await savePendingSpanTargets({
					sourceCollection: originalCollection,
					targetCollections: dirtyTargets,
				});

				CurrentState.CollectionLink.clearModifiedTargets();

				if (targetResult.failedTargetIds.length > 0) {
					NotificationService.error({
						key: T.SPAN_LINK_TARGET_SAVE_ERROR,
					});
				}
			} else if (hasModifiedTargets) {
				// Targets registered but none are dirty (e.g., unlink then re-link same target)
				CurrentState.CollectionLink.clearModifiedTargets();
			}

			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			this.collectionCommitted(result, originalCollection, drawState);
		} catch (error: unknown) {
			this.setSaving(false);

			Services.Log.Logger.error(`${error}`);

			NotificationService.error({
				key: T.COLLECTION_EDIT_SAVE_ERROR,
			});
		}
	}
	@action setSaving(save: boolean) {
		this.isSaving = save;
	}

	private async collectionCommitted(
		collection: CollectionJSONObject,
		originalCollection: CollectionModel,
		drawState: IkeDrawState | undefined
	) {
		if (this.changeState) {
			Services.Analytics.trackTiming(
				TimingEventType.PHOTOMEASURE,
				TimingEventAction.ENTER,
				originalCollection.id,
				new Date(this.changeState.startTime)
			);
			Services.Analytics.trackTiming(
				TimingEventType.PHOTOMEASURE,
				TimingEventAction.EXIT,
				originalCollection.id,
				new Date()
			);
			this.changeState.startTime = Date.now();
		}

		runInAction(() => {
			CurrentState.setInspectedCollection(collection.revision, CurrentState.inspectedCaptureId);

			// PM page will have an IkeDraw. The map page will not.
			if (CurrentState.CollectionEdit.currentDraw != null && drawState) {
				CurrentState.CollectionEdit.currentDraw.state.setFromState(drawState);
			}
			this.setSaving(false);
		});
	}

	@action toggleFieldDetail(fieldId: string) {
		if (this.isFieldDetailShown.get(fieldId)) {
			this.isFieldDetailShown.delete(fieldId);
		} else {
			this.isFieldDetailShown.set(fieldId, true);
		}
	}

	@action async clearModalField(field: CollectionModelField) {
		const response = await ModalService.open({
			title: T.FILE_DELETE_TITLE,
			content: Translate(T.FILE_DELETE_MESSAGE, field.value),
			okName: T.FILE_DELETE,
			hasCancel: true,
		});
		if (response.ok) {
			this.clearField(field);
		}
	}

	@action clearField(field: CollectionModelField) {
		if (
			CaptureProviderHelpers.isCaptureProvider(field) &&
			CurrentState.inspectedCaptureId &&
			field.getOwnedCaptureIds().includes(CurrentState.inspectedCaptureId)
		) {
			CurrentState.inspectedCaptureId = null;
		}

		field.Mutator.clear();

		const state = this.changeState?.getState(field.id);
		if (state != null) {
			state.setValue(null);
		}
	}

	/**
	 * File upload related functions
	 */

	@action.bound onDragOver(e: any, field: CollectionModelFieldFileTool) {
		e.preventDefault();

		const dragArea = document.getElementById(`drag-area-${field.id}`);
		if (dragArea == null) {
			return;
		}

		dragArea.style.backgroundColor = DefaultTheme.color.accent;
	}

	@action.bound onDragLeave(e: any, field: CollectionModelFieldFileTool) {
		e.preventDefault();

		const dragArea = document.getElementById(`drag-area-${field.id}`);
		if (dragArea == null) {
			return;
		}

		dragArea.style.removeProperty('backgroundColor');
	}

	@action.bound onDragEnd(e: any, field: CollectionModelFieldFileTool) {
		e.preventDefault();

		const dragArea = document.getElementById(`drag-area-${field.id}`);
		if (dragArea == null) {
			return;
		}

		dragArea.style.removeProperty('backgroundColor');
	}

	@action.bound selectAnchorField(e: MouseEvent, field: CollectionModelFieldAnchor) {
		e.preventDefault();
		Services.Log.Logger.warn(
			`1. Anchor is edit by user ${Services.User.current?.email.toString()} in department ${Services.Department.current?.name.toString()} of organization ${Services.Department.currentOrg?.name.toString()}`
		);
	}

	@action.bound openFileDialog(e: MouseEvent, field: CollectionModelFieldFileTool) {
		e.preventDefault();

		const fileInput = document.getElementById(field.id);
		if (fileInput == null) {
			throw new Error('Unable to find file input: "' + field.id + "'");
		}

		var evt = document.createEvent('MouseEvents');
		evt.initEvent('click', true, false);
		fileInput.dispatchEvent(evt);
	}

	@action.bound openImageDialog(e: MouseEvent, field: CollectionModelFieldImage) {
		e.preventDefault();

		const imageInput = document.getElementById(field.id);
		if (imageInput == null) {
			throw new Error('Unable to find file input: "' + field.id + "'");
		}

		var evt = document.createEvent('MouseEvents');
		evt.initEvent('click', true, false);
		imageInput.dispatchEvent(evt);
	}

	@action.bound onImage(e: Event, field: CollectionModelFieldImage) {
		const imageInput = e.target as HTMLInputElement;
		if (imageInput == null) {
			throw new Error('Unable to find file input: "' + field.id + "'");
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'image:click', 'button', 1);
		if (imageInput.files) {
			this.loadImages(imageInput.files, field);
		}
	}

	@action.bound onFile(e: Event, field: CollectionModelFieldFileTool) {
		const fileInput = e.target as HTMLInputElement;
		if (fileInput == null) {
			throw new Error('Unable to find file input: "' + field.id + "'");
		}

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:click', 'button', 1);
		if (fileInput.files) {
			this.loadFiles(fileInput.files, field);
		}
	}

	@action.bound update(e: DragEvent, field: CollectionModelFieldFileTool) {
		e.preventDefault();

		if (e.dataTransfer == null) {
			return;
		}

		const files = e.dataTransfer.files;

		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:drop', 'drop', 1);
		this.loadFiles(files, field);
	}

	loadImages(images: FileList, field: CollectionModelFieldImage) {
		this.image = null;

		if (images && images[0]) {
			const image = images[0];
			const validationError = validateImageFile(image);
			if (validationError) {
				NotificationService.error({ key: T[validationError], attrs: { limit: `${MAX_UPLOAD_FILE_SIZE_BYTE} MB` } });
				this.unsetLoading(field.id);
				return;
			}

			this.image = new ImageUploadReader(image, field);
		}
	}

	loadFiles(files: FileList, field: CollectionModelFieldFileTool) {
		this.file = null;

		if (files == null) {
			return;
		}

		if (files.length > 1) {
			Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:count', String(files.length), 1);
			NotificationService.error({ key: T.FILE_NOT_SUPPORTED });
			this.unsetLoading(field.id);
			return;
		}

		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			if (!file) {
				throw new Error(`undefined File recieved from FileList at index ${i}`);
			}

			if (MAX_UPLOAD_FILE_SIZE_BYTE < file.size) {
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:large', 'large', file.size);
				NotificationService.error({ key: T.FILE_TOO_LARGE, attrs: { limit: `${MAX_UPLOAD_FILE_SIZE_MB} MB` } });
				this.unsetLoading(field.id);
				continue;
			}

			const fileType = file.type.toLowerCase();
			if (this.allowedTypes.length > 0 && fileType !== '' && this.allowedTypes.indexOf(fileType) === -1) {
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Upload, 'file:type', fileType, 1);
				NotificationService.error({ key: T.FILE_NOT_SUPPORTED });
				this.unsetLoading(field.id);
				continue;
			}

			this.file = new FileUploadReader(file, field);
		}
	}

	@action.bound async processFile() {
		if (!this.file) {
			return;
		}

		this.setLoading(this.file.field.id);

		if (this.file.buffer) {
			const rest = Services.Department.rest;
			try {
				const attachment = await rest.Attachment.set(this.file.name, this.file.buffer, this.file.type);

				let metadata = {};

				try {
					metadata = await rest.Attachment.parse(attachment.objectId);
				} catch (error) {
					Services.Log.Logger.info(`File cannot be parsed: ${error}`);
				}

				this.file.field.Mutator.manual({
					filename: this.file.name,
					attachmentId: attachment.objectId,
					size: attachment.size,
					metadata,
				});
			} catch (_e) {
				this.unsetLoading(this.file.field.id);
				NotificationService.error({ key: T.ERROR_GENERAL });
			}
		}

		this.unsetLoading(this.file.field.id);
	}

	@action.bound async processImage() {
		if (this.image?.buffer) {
			this.setLoading(this.image.field.id);

			const rest = Services.Department.rest;
			let capture: CaptureFileJSONObject | null = null;
			try {
				capture = await rest.Capture.Image.image(IkeId.v4(), this.image.buffer, 1, new Date());

				this.image.field.Mutator.add(capture.objectId);
			} catch (e) {
				NotificationService.error({ key: T.ERROR_GENERAL });
				Services.Log.Logger.info(`Image cannot be parsed: ${e}`);
			}

			this.unsetLoading(this.image.field.id);
		}
	}

	@action async fileDownload(field: CollectionModelFieldFileTool) {
		if (field.attachmentId) {
			this.setLoading(field.id);

			const token = `Bearer ${Services.SSO.token}`;

			const url = '/v1/file/' + field.attachmentId + '?departmentId=' + Services.Department.current?.objectId;

			const options = {
				headers: {
					Authorization: token,
				},
			};

			// @todo this needs to be refactored to use the rest classes
			fetch(url, options)
				.then((res) => {
					if (!res.ok) {
						Services.Log.Logger.error('File possibly infected');
						NotificationService.error({
							key: T.ERROR_DOWNLOAD_FAIL,
						});
						return null;
					}
					return res.blob();
				})
				.then((blob) => {
					if (blob != null) {
						var file = window.URL.createObjectURL(blob);
						var link = Services.Window.document.createElement('a');
						link.download = field.value ?? '';
						link.href = file;
						Services.Window.document.body.appendChild(link);
						link.click();
						link.remove();
					}
					this.unsetLoading(field.id);
				})
				.catch((e) => {
					Services.Log.Logger.error(e, 'File download error');
					NotificationService.error({
						key: T.ERROR_GENERAL,
					});
					this.unsetLoading(field.id);
				});
		}

		return false;
	}

	setLoading(fieldId: string) {
		this.isInUploadMode = true;
		this.fieldsInLoadingState.push(fieldId);
	}

	unsetLoading(fieldId) {
		this.isInUploadMode = false;
		this.fieldsInLoadingState.splice(this.fieldsInLoadingState.indexOf(fieldId), 1);
	}

	/**
	 * File upload related functions end
	 */

	fillAddressDisabled(field: CollectionModelField): boolean {
		if (field.value) {
			return true;
		}

		if (this.locationForField(field) == null) {
			return true;
		}

		if (!this.isEditing || this.isSaving) {
			return true;
		}

		return false;
	}

	geocodingButtonTitle(field: CollectionModelField): string {
		if (field.value) {
			return 'Already has value';
		}

		if (this.locationForField(field) == null) {
			return 'There is no location';
		}

		return 'Get address';
	}

	location(): GeoPointJSON | null {
		const revision = CurrentState.inspectedCollectionRevision;
		if (!revision) {
			return null;
		}

		const collectionSummary = AppStore.CollectionSummary.get(revision);
		const location = collectionSummary?.location;

		if (!(location && location.coordinates)) {
			return null;
		}

		return location;
	}

	/**
	 * For an address-lookup field nested in a subform, prefer the location field within
	 * the same subform instance. For top-level address fields, use the persisted collection
	 * summary location.
	 */
	locationForField(field: CollectionModelField): GeoPointJSON | null {
		if (field.parent.parent != null) {
			const subFormLocation = field.parent.location;
			if (subFormLocation) {
				return ParseJSON.toGeoPoint(subFormLocation);
			}
		}
		return this.location();
	}

	@action async fillAddress(field: CollectionModelField): Promise<void> {
		const location = this.locationForField(field);
		if (location == null) {
			return;
		}

		if (!this.changeState) {
			return;
		}

		const addressFields = await Services.Geocoder.getAddress(location);
		const address = addressFields.address;
		const fieldState = this.changeState.getState(field.id);
		// Sync field.value before inputState — viewEdit re-syncs inputState ← field.value each
		// render, which can race with setValue's onValue reaction and reset the new value.
		if (CollectionModelFieldText.isTextField(field)) {
			field.Mutator.manual(address);
		}
		fieldState.setValue(address);
		if (address === Translate(T.ERROR_GENERAL)) {
			fieldState.setError(address);
		}

		const metadata = field.collection.schema.metadata;
		const city = addressFields.city;
		const state = addressFields.state;

		if (city && metadata.options.address_lookup?.city) {
			const cityFieldId = metadata.options.address_lookup.city.field;
			const cityFieldState = this.changeState.getState(CollectionModelField.getStateId(field.parent, cityFieldId));
			if (cityFieldState) {
				const cityField = field.parent.getOptionalField<CollectionModelField>(cityFieldId);
				if (cityField && CollectionModelFieldText.isTextField(cityField)) {
					cityField.Mutator.manual(city);
				}
				cityFieldState.setValue(city);
			}
		}

		if (state && metadata.options.address_lookup?.state) {
			const stateFieldId = metadata.options.address_lookup.state.field;
			const stateFieldState = this.changeState.getState(CollectionModelField.getStateId(field.parent, stateFieldId));
			if (stateFieldState) {
				const stateField = field.parent.getOptionalField<CollectionModelField>(stateFieldId);
				if (stateField && CollectionModelFieldText.isTextField(stateField)) {
					stateField.Mutator.manual(state);
				}
				stateFieldState.setValue(state);
			}
		}
	}

	@action clearError(field: CollectionModelField) {
		if (!this.changeState) {
			return;
		}
		const fieldState = this.changeState.getState(field.id);
		fieldState.setValue('');
		fieldState.setError('');
	}

	clearErrorDisabled(field: CollectionModelField): boolean {
		if (!this.isEditing || this.isSaving) {
			return true;
		}

		if (!this.changeState) {
			return true;
		}

		const state = this.changeState.getState(field.id);
		if (state.hasError() === true) {
			return false;
		}

		return true;
	}

	isExpanded(instanceId: string): boolean {
		if (this.isAllExpanded) {
			return this.isFormOpen.get(instanceId) !== false;
		}

		return this.isFormOpen.get(instanceId) ?? false;
	}

	@action toggleExpanded(e: MouseEvent, instanceId: string) {
		e.preventDefault();
		let prev = this.isFormOpen.get(instanceId);
		if (prev == null) {
			prev = this.isAllExpanded;
		}
		this.isFormOpen.set(instanceId, !prev);
		return false;
	}

	@action setZoomWindow(show: boolean) {
		this.isZoomShown = show;
	}

	@action.bound zoomToCollection() {
		if (CurrentState.inspectedCollectionRevision) {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			this.mapState.zoomToCollection(CurrentState.inspectedCollectionRevision);
		}
	}

	@action.bound toggleMetaInfo() {
		this.isMetadataShown = !this.isMetadataShown;
		this.isZoomShown = false;
	}

	@action.bound toggleZoomWindow() {
		this.isZoomShown = !this.isZoomShown;
		this.isMetadataShown = false;
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Collection,
			`zoom:${this.isZoomShown ? 'show' : 'hide'}`,
			this.collection?.revision
		);
	}

	@action.bound toggleExpandAll() {
		this.isAllExpanded = !this.isAllExpanded;
		this.isFormOpen.clear();
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Collection,
			`form:${this.isAllExpanded ? 'expand' : 'collapse'}`,
			this.collection?.revision
		);
	}

	@action.bound enterEditMode() {
		if (!CurrentState.inspectedCollectionRevision) {
			return;
		}
		const collection = AppStore.Collection.get(CurrentState.inspectedCollectionRevision);
		if (collection != null) {
			Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'Collection:edit', collection.uuid, 1);
		}
		this.isInEditMode = true;
	}

	@action exitEditMode() {
		if (CurrentState.CollectionEdit.isDirty) {
			throw new Error('Must discard changes before exiting collection edit mode');
		}

		this.isInEditMode = false;
	}

	@action.bound private stopNavigation() {
		return CurrentState.CollectionEdit.isDirty || CurrentState.CollectionLink.hasDirtyTargets;
	}

	async beginSpanLink(c: CollectionModelBase) {
		const collection = c.collection;
		const state = new SpanLinkChooserState(collection);

		const result = await ModalService.open({
			title: MultiListChooserTitle.view(state),
			content: () => MultiListChooserContent.view(state),
			className: MultiListChooserCSS.modal,
			hasCancel: true,
		});

		if (!result.ok) {
			Services.Analytics.trackEvent(
				Services.Analytics.EventCategory.Mutate,
				'span:link:cancel',
				this.collection?.revision
			);
			return;
		}

		if (state.value == null) {
			state.selectDefaultValue();
		}

		const spanLinkRecord = state.getSelectedSpanLinkRecord();
		if (spanLinkRecord == null) {
			return;
		}

		runInAction(() => {
			try {
				const newSubformInstance = Services.SubFormLink.copySubForm(collection, spanLinkRecord);

				NotificationService.success({
					key: T.SPAN_LINK_SUCCESS,
					attrs: {
						toSpan: newSubformInstance.title,
						fromCollection: spanLinkRecord.collection.title,
					},
				});

				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'span:link', this.collection?.revision);
			} catch (e: unknown) {
				Services.Log.Logger.error(`Span link failed: ${e}`);
				NotificationService.error({
					key: T.ERROR_GENERAL,
				});
			}
		});
	}

	@computed get matches(): FieldJoinMatch[] {
		if (this.collection == null || !Services.Measurement.isFormSupported(this.collection.schema)) {
			return [];
		}

		return Services.Measurement.getFieldJoins(this.collection);
	}

	getJoin(field: CollectionModelField): FieldJoin | null {
		for (const match of this.matches) {
			const join = match.getJoin(field.parent.instanceId, field.schema.id);
			if (join != null) {
				return join;
			}
		}

		return null;
	}

	getOppositeJoin(field: CollectionModelField): FieldJoin | null {
		for (const match of this.matches) {
			const join = match.getOppositeJoin(field.parent.instanceId, field.schema.id);
			if (join != null) {
				return join;
			}
		}

		return null;
	}

	async chooseLinkingCollection(): Promise<CollectionModel | null> {
		return new Promise<CollectionModel | null>((resolve) => {
			const disposer = reaction(
				() => CurrentState.CollectionLink.isLinking,
				() => {
					disposer();

					const revision = CurrentState.CollectionLink.collectionRevision;
					if (revision == null) {
						return resolve(null);
					}

					const collection = AppStore.Collection.get(revision);
					return resolve(collection);
				}
			);
		});
	}

	canInspectCollection(summary: CollectionSummaryJSONObject): boolean {
		if (summary == null) {
			return false;
		}
		const id = summary.tags[0];
		if (!id) {
			return false;
		}
		const tag = AppStore.Tag.get(id);
		if (!tag) {
			return false;
		}
		return Services.Search.includesTag(tag);
	}

	inspectCollection(revision: string) {
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.mapState.zoomToCollection(revision);
	}
}
